import { SHARED_ROUTES } from '@aibees/shared/constants';
import { capitalize, classnames } from '@aibees/shared/utils';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DialogTitle } from '../dialog-title';
import { Icon } from '../icon';
import {
  UPGRADE_PLAN_FEATURES,
  UPGRADE_PLAN_HEADER_TITLE,
  UPGRADE_PLAN_IMAGE,
  UPGRADE_PLAN_SUBTITLE,
} from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type { UpgradePlanDialogProps as Props } from './types';

export const UpgradePlanDialog = memo(
  ({
    className,
    state,
    planName,
    headerTitle = UPGRADE_PLAN_HEADER_TITLE,
    subtitle = UPGRADE_PLAN_SUBTITLE,
    features = UPGRADE_PLAN_FEATURES,
  }: Props) => {
    const navigate = useNavigate();

    const handleUpgrade = () => {
      state.onFalse();
      navigate(SHARED_ROUTES.priceAndPlan);
    };

    return (
      <Styled.Wrapper
        className={classnames('UpgradePlanDialog', className)}
        sx={SX.root}
        open={state.value}
        onClose={state.onFalse}
        fullWidth
        maxWidth="xxs"
      >
        <DialogTitle
          title={headerTitle}
          icon="rocket-launch"
          onClose={state.onFalse}
        />

        <DialogContent>
          <Stack gap={1}>
            <Styled.Image src={UPGRADE_PLAN_IMAGE} alt="" sx={SX.image} />

            <Stack gap={2}>
              <Stack gap={0.5}>
                <Typography variant="body2" fontWeight={700} color="grey.700">
                  {planName ? (
                    <>
                      Keep going with{' '}
                      <Box component="span" color="primary.500">
                        {capitalize(planName)}
                      </Box>{' '}
                      or higher plan.
                    </>
                  ) : (
                    'Keep going with a higher plan.'
                  )}
                </Typography>

                <Typography variant="body4" fontWeight={500} color="grey.500">
                  {subtitle}
                </Typography>
              </Stack>

              <Stack gap={1}>
                {features.map((feature) => (
                  <Stack
                    key={feature}
                    direction="row"
                    alignItems="center"
                    gap={1}
                  >
                    <Icon
                      name="check-mark-fill"
                      size={18}
                      color="success.500"
                      sxx={SX.checkIcon}
                    />
                    <Typography
                      variant="body4"
                      fontWeight={500}
                      color="grey.700"
                    >
                      {feature}
                    </Typography>
                  </Stack>
                ))}

                <Typography variant="body4" fontWeight={500} color="grey.500">
                  And much more...
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            variant="text"
            color="default"
            size="small"
            onClick={state.onFalse}
          >
            Maybe Later
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            endIcon={<Icon name="arrow-right" />}
            onClick={handleUpgrade}
          >
            Upgrade Plan
          </Button>
        </DialogActions>
      </Styled.Wrapper>
    );
  }
);

UpgradePlanDialog.displayName = 'UpgradePlanDialog';
